/** @format */

import Hotjar from "@hotjar/browser";
import { DirectionProvider } from "@radix-ui/react-direction";
import { useHead } from "@unhead/react";
import { isSignInWithEmailLink } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore/lite";
import React, { lazy, useEffect, useRef, useState } from "react";
import { isDesktop, isMobileOnly, isTablet } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  AccessibilityStatement,
  GlobalStyle,
  IconSprite,
  Imprint,
  MaintenanceModeTemplate,
  PreferenceBanner,
  PrivacyPolicy,
  PrivacySettings,
  ResetPassword,
  SimpleLoader,
  TermsAndConditions,
  useAuthModal,
  VerifyEmail,
} from "senf-atomic-design-system";
import { useModals } from "senf-atomic-design-system/src/components/molecules/modalStack/ModalProvider";
import UpdateModal from "senf-atomic-design-system/src/components/special/update/UpdateModal";
import { usePreventInterfaceZoom } from "senf-atomic-design-system/src/hooks/usePreventInterfaceZoom";
import { useThemeColor } from "senf-atomic-design-system/src/hooks/useThemeColor";
import { addModeToSessionStorage, auth, db, useAuthContext } from "senf-shared";
import { readEnvBool } from "senf-shared/utils/helpers";
import { useGetTranslations } from "senf-shared/utils/hooks";
import i18n from "senf-shared/utils/i18n";
import { initSentry, setUser as setSentryUser } from "senf-shared/utils/sentry";
import Cookies from "universal-cookie";
import useAccessibility from "./hooks/useAccessibility";
import useUtmVisitor from "./hooks/useUtmVisitor";
import IdeaMapPage from "./pages/IdeaMapPage";
import ProjectInfoPage from "./pages/ProjectInfoPage";
import ProjectResultsPage from "./pages/ProjectResultsPage";
import Reroute from "./pages/Reroute";
// import Tracking from "./pages/Tracking";
import Survey from "./pages/Survey";
import { setCookies } from "./redux/actions/cookiesActions";
import { setProject } from "./redux/actions/projectActions";
import store from "./redux/store";

const cookies = new Cookies();
window.store = store;
// Helper function to get accurate viewport dimensions on mobile devices
const getViewportDimensions = () => {
  // Use window.innerHeight for height to prevent resizing when keyboard opens
  // Use visualViewport width if available for accurate width on mobile
  return {
    height: window.innerHeight,
    width: window.visualViewport
      ? window.visualViewport.width
      : window.innerWidth,
  };
};

const setViewportProperties = () => {
  const { height, width } = getViewportDimensions();
  document.documentElement.style.setProperty("--vh", `${height}px`);
  document.documentElement.style.setProperty("--h-screen", `${height}px`);
  document.documentElement.style.setProperty("--w-screen", `${width}px`);
};

// Set initial values
setViewportProperties();

const App = () => {
  // Build 273
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { user, getUserData, setUser } = useAuthContext();
  const { openModal, closeModal } = useModals();
  const magicLinkModalOpenedRef = useRef(false);
  const navigate = useNavigate();
  const { consent_settings } = useSelector((state) => state.data);
  const project = useSelector((state) => state.data.project);
  const { openAuthModal } = useAuthModal(project);
  const urlParams = new URLSearchParams(window.location.search);
  const utm_id = urlParams.get("utm_id");
  const privacyConfigurator = urlParams.get("privacyConfigurator");
  const noCrawl = readEnvBool("VITE_NO_CRAWL");
  const statsEnabled = readEnvBool("VITE_STATS");
  const hasConsentAll = consent_settings === "all";

  const { fetchTranslations } = useGetTranslations(project);

  useUtmVisitor();
  useAccessibility();

  // Prevent interface zoom on mobile while allowing map pinch gestures
  usePreventInterfaceZoom();

  // SEO and analytics setup via Unhead
  const shouldTrack = statsEnabled && hasConsentAll;
  const websiteId =
    import.meta.env.VITE_APP_MODE === "staging" ||
    import.meta.env.VITE_APP_MODE === "development"
      ? "51df5aa1-e967-429a-adf5-cc41f0a86a49"
      : "f5d737f5-ec96-45e8-843c-ffef8e669a55";

  useHead({
    meta: [
      // disable google indexing for demo.senf.app
      ...(noCrawl ? [{ name: "robots", content: "noindex, nofollow" }] : []),
    ],
  });

  useHead({
    script: shouldTrack
      ? [
          {
            key: "senf-umami",
            src: "https://senf-umami.netlify.app/senf-stat.js",
            async: true,
            defer: true,
            "data-website-id": websiteId,
          },
        ]
      : [],
  });

  useEffect(() => {
    if (user?.userId) {
      setSentryUser({
        id: user.userId,
        email: user.email,
        username: user.handle,
      });
    } else {
      setSentryUser(null);
    }
  }, [user]);

  if (
    import.meta.env.VITE_APP_MODE !== "staging" &&
    import.meta.env.VITE_APP_MODE !== "development" &&
    localStorage?.getItem("developer") === null
  ) {
    const siteId = 3778185;
    const hotjarVersion = 6;
    Hotjar.init(siteId, hotjarVersion);
  } else {
    // const siteId = 3778203;
  }

  useEffect(() => {
    addModeToSessionStorage();

    if (document?.referrer && !localStorage.getItem("referrer"))
      localStorage.setItem("referrer", new URL(document?.referrer).hostname);

    const handleResize = () => setViewportProperties();

    // Listen for regular window resize only (not visualViewport to prevent layout shifts when keyboard opens)
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // If we land on a Firebase email sign-in link, open the auth modal on top of the current page
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (magicLinkModalOpenedRef.current) return;
    if (!isSignInWithEmailLink(auth, window.location.href)) return;
    magicLinkModalOpenedRef.current = true;
    // Defer to next tick to avoid interfering with initial render
    setTimeout(() => {
      openAuthModal("login", {
        success: () => closeModal(),
        error: () => closeModal(),
        handleClose: () => closeModal(),
        simple: true,
        id: "authMagicLink",
        onClose: () => {
          magicLinkModalOpenedRef.current = false;
        },
      });
    }, 0);
  }, [project, openAuthModal, closeModal]);

  // we could use user from authContext  without setting it to redux ,but
  // @todo: we need to figure out how to set likes and dislikes in authContext
  // without refreshing the page

  const handleSetPreferences = (consent_settings) => {
    dispatch(setCookies(consent_settings));
    // Initialize Sentry when user accepts all cookies
    if (consent_settings === "all") {
      initSentry();
    }
  };

  useEffect(() => {
    if (cookies.get("consent_settings") === "all") {
      dispatch(setCookies("all"));
      // Initialize Sentry when user has accepted all cookies
      initSentry();
    } else if (cookies.get("consent_settings") === "minimum") {
      dispatch(setCookies("minimum"));
    } else {
      openModal(
        <PreferenceBanner handleSetPreferences={handleSetPreferences} />,
        {
          size: "md",
          id: "preferencesDialog",
          onClose: false,
          preventClose: true,
          zIndex: 1000,
          ariaTitle: "Privacy Preferences",
        },
      );
    }
  }, [cookies]);

  useEffect(() => {
    if (privacyConfigurator === "true") {
      openModal(
        <PrivacySettings handleSetPreferences={handleSetPreferences} />,
        {
          size: "lg",
          id: "privacyConfigurator",
          zIndex: 1001,
        },
      );

      setTimeout(() => {
        urlParams.delete("privacyConfigurator");
        const newUrl = window.location.pathname;
        window.history.replaceState(null, "", newUrl);
      }, 1000);
    }
  }, [privacyConfigurator]);

  useEffect(() => {
    if (!["projects"].includes(pathname.split("/")[1])) return;
    const projectId = pathname.split("/")[2];
    dispatch(setProject(projectId, navigate));
  }, []);
  const customTheme = useThemeColor(project);

  useEffect(() => {
    if (project?.organizationId) {
      setTimeout(() => {
        fetchTranslations(i18n.resolvedLanguage);
      }, 0);
    }
  }, [project?.organizationId, i18n?.resolvedLanguage]);

  useEffect(() => {
    if (project?.projectId) {
      const preference = localStorage.getItem("language");
      if (preference && project?.settings?.languages?.includes(preference)) {
        i18n.changeLanguage(preference);
      } else {
        i18n.changeLanguage(project?.settings?.originalLanguage || "de");
      }
    }
  }, [project]);

  const addUserToProject = async (user, project) => {
    const participantData = {
      userId: user?.userId,
      visitorId: localStorage.getItem("visitorId") || null,
      age: user?.age || null,
      gender: user?.gender || null,
      postcode: user?.postcode || null,
      createdAt: new Date().toISOString(),
      handle: user?.handle || null,
      url: window.location.href,
      device: isMobileOnly
        ? "mobile"
        : isTablet
          ? "tablet"
          : isDesktop
            ? "desktop"
            : "other",
      projectId: project?.projectId,
      referrer: localStorage.getItem("referrer") || "",
      utm_id:
        typeof utm_id !== "undefined"
          ? utm_id
          : localStorage.getItem("utm_id") || "",
    };

    try {
      const participantDocRef = doc(
        db,
        `organizations/${project.organizationId}/projects/${project.projectId}/participants/${user.userId}`,
      );

      const docSnap = await getDoc(participantDocRef);

      if (!docSnap.exists()) {
        // Create the document if it doesn't exist
        await setDoc(participantDocRef, participantData);
      } else {
        // Compare the existing data with the new data
        const existingData = docSnap.data();

        // Function to compare data objects
        const isDataChanged = (newData, oldData) => {
          for (const key in newData) {
            if (newData[key] !== oldData[key]) {
              return true;
            }
          }
          return false;
        };

        if (isDataChanged(participantData, existingData)) {
          // Update the document only if data has changed
          await setDoc(participantDocRef, participantData, { merge: true });
        }
      }
    } catch (error) {
      console.error(`Error adding or updating user in project: ${error}`);
    }
  };

  useEffect(() => {
    if (user && project?.projectId) {
      addUserToProject(user, project);
    }
  }, [user, project?.projectId]);

  const handleNavigateBack = () => {
    window.close();
    // const pathArray = window.location.pathname.split("/");
    // const newPath = pathArray.slice(0, -1).join("/");
    // navigate(newPath); // Redirect to the new URL without the last item
  };

  useEffect(() => {
    if (user?.userId && project?.projectId) {
      getUserData(user?.userId, project)
        .then(setUser)
        .catch(() => setUser(null));
    }
  }, [project?.projectId, user?.userId]);

  return (
    <DirectionProvider dir={document.dir}>
      <GlobalStyle custom={customTheme} />

      <IconSprite />

      {/* {localStorage?.getItem("developer") === "true" ? null : (
        <MaintenanceModeTemplate />
      )} */}
      <UpdateModal />
      <Routes>
        <Route
          exact
          path="/projects/:projectId/info"
          element={
            project?.projectId ? (
              <ProjectInfoPage data={project} />
            ) : (
              <SimpleLoader />
            )
          }
        />
        <Route
          exact
          path="/projects/:projectId/info/:pageSlug"
          element={
            project?.projectId ? (
              <ProjectInfoPage data={project} />
            ) : (
              <SimpleLoader />
            )
          }
        />
        <Route
          exact
          path="/projects/:projectId/results"
          element={
            project?.projectId ? <ProjectResultsPage /> : <SimpleLoader />
          }
        />
        <Route
          exact
          path="/projects/:projectId/"
          element={project?.projectId ? <Reroute /> : <SimpleLoader />}
        />
        <Route
          exact
          path="/projects/:projectId/ideaMaps/:ideaMapId"
          element={project?.projectId ? <IdeaMapPage /> : <SimpleLoader />}
        />
        <Route
          exact
          path="/projects/:projectId/ideaMaps/:ideaMapId/addIdea"
          element={project?.projectId ? <IdeaMapPage /> : <SimpleLoader />}
        />
        <Route
          exact
          path="/projects/:projectId/ideaMaps/:ideaMapId/:ideaId"
          element={project?.projectId ? <IdeaMapPage /> : <SimpleLoader />}
        />
        <Route
          exact
          path="/projects/:projectId/ideaMaps/:ideaMapId/:ideaId/editIdea"
          element={project?.projectId ? <IdeaMapPage /> : <SimpleLoader />}
        />
        <Route
          exact
          path="/projects/:projectId/survey/:surveyId"
          element={project?.projectId ? <Survey /> : <SimpleLoader />}
        />
        <Route
          exact
          path="/projects/:projectId/imprint"
          element={
            <Imprint
              handleNavigateBack={handleNavigateBack}
              project={project}
            />
          }
        />
        <Route
          exact
          path="/projects/:projectId/privacyPolicy"
          element={
            <PrivacyPolicy
              handleNavigateBack={handleNavigateBack}
              url={`${window?.location?.origin}/projects/${project?.projectId}`}
              project={project}
            />
          }
        />{" "}
        <Route
          exact
          path="/projects/:projectId/accessibilityStatement"
          element={
            <AccessibilityStatement
              handleNavigateBack={handleNavigateBack}
              url={`${window?.location?.origin}/projects/${project?.projectId}`}
              project={project}
            />
          }
        />{" "}
        <Route
          exact
          path="/projects/:projectId/terms"
          element={
            <TermsAndConditions
              handleNavigateBack={handleNavigateBack}
              project={project}
            />
          }
        />
        <Route exact path="/profile/:profileId" element={<IdeaMapPage />} />
        <Route
          exact
          path="/profile/:profileId/:ideaId"
          element={<IdeaMapPage />}
        />
        <Route
          exact
          path="/profile/:profileId/:ideaId/:editIdeaId"
          element={<IdeaMapPage />}
        />
        <Route
          exact
          path="/organizations/:organizationId"
          element={<IdeaMapPage />}
        />
        {/* <Route
          exact
          path="/tracking"
          element={<Tracking />}
        /> */}
        {/* <Route
          exact
          path="/l/:urlId"
          element={<L />}
        /> */}
        <Route exact path="/resetPassword" element={<ResetPassword />} />
        <Route exact path="/verify" element={<VerifyEmail />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </DirectionProvider>
  );
};

export default App;
